import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { buildPath } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import { FolderBusinessEntityActorsActionKind } from '../enums/action-kind.enum';
import type { ActorsState } from '../types/state.type';
import type { ActorsAction } from '../types/actions.type';
import type { Props } from '../types/props.type';

function actorsReducer(state: ActorsState, action: ActorsAction): ActorsState {
  const actionType = action.type;

  switch (actionType) {
    case FolderBusinessEntityActorsActionKind.ClearState:
      return (
        action.state ?? {
          idToRemove: null,
          idToUpdate: null,
          isCreateDrawerOpen: false,
          isFlushDialogOpen: false,
          isCustomizeDialogOpen: false,
          isRemoveDialogOpen: false,
          isUpdateDrawerOpen: false,
        }
      );
    case FolderBusinessEntityActorsActionKind.StartCreate:
      return {
        ...state,
        idToRemove: null,
        idToUpdate: null,
        isCreateDrawerOpen: true,
        isFlushDialogOpen: false,
        isCustomizeDialogOpen: false,
        isRemoveDialogOpen: false,
        isUpdateDrawerOpen: false,
      };
    case FolderBusinessEntityActorsActionKind.StartFlush:
      return {
        ...state,
        idToRemove: null,
        idToUpdate: null,
        isCreateDrawerOpen: false,
        isFlushDialogOpen: true,
        isCustomizeDialogOpen: false,
        isRemoveDialogOpen: false,
        isUpdateDrawerOpen: false,
      };
    case FolderBusinessEntityActorsActionKind.StartCustomize:
      return {
        ...state,
        idToRemove: null,
        idToUpdate: null,
        isCreateDrawerOpen: false,
        isFlushDialogOpen: false,
        isCustomizeDialogOpen: true,
        isRemoveDialogOpen: false,
        isUpdateDrawerOpen: false,
      };
    case FolderBusinessEntityActorsActionKind.StartRemove:
      return {
        ...state,
        idToRemove: action.payload,
        idToUpdate: null,
        isCreateDrawerOpen: false,
        isFlushDialogOpen: false,
        isCustomizeDialogOpen: false,
        isRemoveDialogOpen: true,
        isUpdateDrawerOpen: false,
      };
    case FolderBusinessEntityActorsActionKind.StartUpdate:
      return {
        ...state,
        idToRemove: null,
        idToUpdate: action.payload,
        isCreateDrawerOpen: false,
        isFlushDialogOpen: false,
        isCustomizeDialogOpen: false,
        isRemoveDialogOpen: false,
        isUpdateDrawerOpen: true,
      };
    case FolderBusinessEntityActorsActionKind.EndCreate:
      return {
        ...state,
        isCreateDrawerOpen: false,
      };
    case FolderBusinessEntityActorsActionKind.EndFlush:
      return {
        ...state,
        isFlushDialogOpen: false,
      };
    case FolderBusinessEntityActorsActionKind.EndCustomize:
      return {
        ...state,
        isCustomizeDialogOpen: false,
      };
    case FolderBusinessEntityActorsActionKind.EndRemove:
      return {
        ...state,
        idToRemove: null,
        isRemoveDialogOpen: false,
      };
    case FolderBusinessEntityActorsActionKind.EndUpdate:
      return {
        ...state,
        idToUpdate: null,
        isUpdateDrawerOpen: false,
      };
    default:
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new Error(`Unknown action type: ${actionType}`);
  }
}

export const useActorsReducer = ({
  paths,
  queryKeysToInvalidate,
}: Readonly<Props>) => {
  const { pathname: currentPath } = useLocation();
  const { actorId } = useParams();
  const queryClient = useQueryClient();

  const resolvedState = useMemo(
    () => ({
      idToRemove:
        currentPath === paths?.editDelete && actorId ? (actorId as Id) : null,
      idToUpdate:
        currentPath === paths?.editModify && actorId ? (actorId as Id) : null,
      isCreateDrawerOpen: currentPath === paths?.editNew,
      isFlushDialogOpen: currentPath === paths?.editFlush,
      isCustomizeDialogOpen: currentPath === paths?.editCustomize,
      isRemoveDialogOpen: currentPath === paths?.editDelete,
      isUpdateDrawerOpen: currentPath === paths?.editModify,
    }),
    [
      actorId,
      paths?.editDelete,
      paths?.editFlush,
      paths?.editCustomize,
      paths?.editModify,
      paths?.editNew,
      currentPath,
    ],
  );

  const [state, dispatch] = useReducer(actorsReducer, resolvedState);

  useEffect(() => {
    dispatch({
      type: FolderBusinessEntityActorsActionKind.ClearState,
      state: resolvedState,
    });
  }, [resolvedState]);

  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const invalidateQueries = useCallback(() => {
    queryKeysToInvalidate.forEach((queryKey) => {
      void queryClient.invalidateQueries({ queryKey });
    });
  }, [queryClient, queryKeysToInvalidate]);

  const handleClearState = useCallback(() => {
    paths
      ? void navigate(paths.edit)
      : dispatch({ type: FolderBusinessEntityActorsActionKind.ClearState });
  }, [paths, navigate]);

  const handleStartCreate = useCallback(() => {
    paths
      ? void navigate(paths.editNew)
      : dispatch({ type: FolderBusinessEntityActorsActionKind.StartCreate });
  }, [navigate, paths]);

  const handleStartFlush = useCallback(() => {
    paths
      ? void navigate(paths.editFlush)
      : dispatch({ type: FolderBusinessEntityActorsActionKind.StartFlush });
  }, [navigate, paths]);

  const handleStartCustomize = useCallback(() => {
    paths
      ? void navigate(paths.editCustomize)
      : dispatch({
          type: FolderBusinessEntityActorsActionKind.StartCustomize,
        });
  }, [navigate, paths]);

  const handleStartRemove = useCallback(
    (id: Id) => {
      paths
        ? void navigate(buildPath(paths.editDelete, { actorId: id }))
        : dispatch({
            type: FolderBusinessEntityActorsActionKind.StartRemove,
            payload: id,
          });
    },
    [navigate, paths],
  );

  const handleStartUpdate = useCallback(
    (id: Id) => {
      paths
        ? void navigate(buildPath(paths.editModify, { actorId: id }))
        : dispatch({
            type: FolderBusinessEntityActorsActionKind.StartUpdate,
            payload: id,
          });
    },
    [navigate, paths],
  );

  const handleCreateSuccess = useCallback(() => {
    paths
      ? (() => {
          invalidateQueries();
          enqueueSnackbar("L'acteur a bien été créé.");
          void navigate(paths.edit);
        })()
      : dispatch({ type: FolderBusinessEntityActorsActionKind.EndCreate });
  }, [enqueueSnackbar, navigate, paths, invalidateQueries]);

  const handleFlushSuccess = useCallback(() => {
    paths
      ? (() => {
          invalidateQueries();
          enqueueSnackbar('La liste a bien été supprimée.');
          void navigate(paths.edit);
        })()
      : dispatch({ type: FolderBusinessEntityActorsActionKind.EndFlush });
  }, [enqueueSnackbar, navigate, paths, invalidateQueries]);

  const handleCustomizeSuccess = useCallback(() => {
    paths
      ? (() => {
          invalidateQueries();
          enqueueSnackbar('La liste a bien été copiée.');
          void navigate(paths.edit);
        })()
      : dispatch({ type: FolderBusinessEntityActorsActionKind.EndCustomize });
  }, [enqueueSnackbar, navigate, paths, invalidateQueries]);

  const handleRemoveSuccess = useCallback(() => {
    paths
      ? (() => {
          invalidateQueries();
          enqueueSnackbar("L'acteur a bien été supprimé.");
          void navigate(paths.edit);
        })()
      : dispatch({ type: FolderBusinessEntityActorsActionKind.EndRemove });
  }, [enqueueSnackbar, navigate, paths, invalidateQueries]);

  const handleUpdateSuccess = useCallback(() => {
    paths
      ? (() => {
          invalidateQueries();
          enqueueSnackbar("L'acteur a bien été mis à jour.");
          void navigate(paths.edit);
        })()
      : dispatch({ type: FolderBusinessEntityActorsActionKind.EndUpdate });
  }, [enqueueSnackbar, navigate, paths, invalidateQueries]);

  return {
    state,
    dispatch,
    handleClearState,
    handleStartCreate,
    handleStartFlush,
    handleStartCustomize,
    handleStartRemove,
    handleStartUpdate,
    handleCreateSuccess,
    handleFlushSuccess,
    handleCustomizeSuccess,
    handleRemoveSuccess,
    handleUpdateSuccess,
  };
};
